"use client";
import React, { useState, useEffect, useCallback, useRef } from "react";
import Image from "next/image";
import { useRouter } from "next/navigation";
import SearchIcon from "@/assets/icons/search-icon.png";
import Banner from "@/components/Blogs/Banner";
import BlogCards from "@/components/Blogs/BlogCards";
import Category from "@/components/Blogs/Category";
import Pagination from "@/components/Pagination";
import Breadcrumbs from "@/components/Breadcrumbs";
import {
  GetAllBlog,
  getCategories,
  fetchSubcategory,
} from "@/helpers/BlogApis";
import "@/app/blog/blog.css";

// Skeleton loader component
const BlogCardSkeleton = () => (
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((i) => (
      <div key={i} className="animate-pulse">
        <div className="bg-gray-200 h-48 rounded-t-lg"></div>
        <div className="p-4 space-y-3">
          <div className="h-4 bg-gray-200 rounded w-3/4"></div>
          <div className="h-3 bg-gray-200 rounded w-full"></div>
          <div className="h-3 bg-gray-200 rounded w-5/6"></div>
        </div>
      </div>
    ))}
  </div>
);

const Blogs = ({ initialData, initialCategories, initialPage = 1 }) => {
  const router = useRouter();
  const isInitialMount = useRef(true);

  const [activeCategory, setActiveCategory] = useState({ name: "All Blogs", id: null });
  const [selectedSubcategory, setSelectedSubcategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [blogs, setBlogs] = useState(initialData?.data || []);
  const [categories, setCategories] = useState([
    { name: "All Blogs", id: null, description: "View all blog posts" },
  ]);


  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const data = await getCategories();
        if (data?.data) {
          setCategories([
            { name: "All Blogs", id: null, description: "View all blog posts" },
            ...data.data
          ]);
        } else {
          setCategories([
            { name: "All Blogs", id: null, description: "View all blog posts" },
          ]);
        }
      } catch (error) {
        console.error("Error fetching categories:", error);
        setCategories([
          { name: "All Blogs", id: null, description: "View all blog posts" },
        ]);
      }
    };

    fetchCategories();
  }, []);
  const [subcategories, setSubcategories] = useState([]);
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [totalPages, setTotalPage] = useState(
    initialData?.meta?.pagination?.pageCount || 1,
  );
  const [isLoadingBlogs, setIsLoadingBlogs] = useState(false);
  const pageSize = 12;
  const skipUrlSyncRef = useRef(false);

  const buildHref = useCallback(
    (page) => {
      const params = new URLSearchParams();

      if (activeCategory.name && activeCategory.name !== "All Blogs") {
        params.set("category", activeCategory.name);
      }
      if (searchQuery) {
        params.set("search", searchQuery);
      }
      if (selectedSubcategory?.id) {
        params.set("subcategoryId", String(selectedSubcategory.id));
      }

      if (page && page > 1) {
        params.set("page", String(page));
      }

      const qs = params.toString();
      return `/blog${qs ? `?${qs}` : ""}`;
    },
    [activeCategory, searchQuery, selectedSubcategory],
  );

  const fetchBlogs = useCallback(
    async (
      categoryName,
      page = 1,
      pageSize = 12,
      search = "",
      subcategoryId = null,
    ) => {
      setIsLoadingBlogs(true);
      try {
        const data = await GetAllBlog(
          categoryName,
          page,
          pageSize,
          search,
          subcategoryId,
        );
        if (data) {
          setBlogs(data.data || []);
          setTotalPage(data.meta?.pagination?.pageCount || 1);
        }
      } catch (error) {
        console.error("Error fetching blogs:", error);
      } finally {
        setIsLoadingBlogs(false);
      }
    },
    [],
  );

  const fetchSubcategories = useCallback(async (categoryId) => {
    if (categoryId) {
      const data = await fetchSubcategory(categoryId);
      setSubcategories(data?.data || []);
    } else {
      setSubcategories([]);
    }
  }, []);

  const updateURL = useCallback(
    (params) => {
      const current = new URLSearchParams(window.location.search);
      Object.entries(params).forEach(([key, value]) => {
        if (
          value === null ||
          value === undefined ||
          value === "" ||
          value === "All Blogs"
        ) {
          current.delete(key);
        } else {
          current.set(key, String(value));
        }
      });
      const search = current.toString();
      const query = search ? `?${search}` : "";
      router.push(`${window.location.pathname}${query}`, { scroll: false });
    },
    [router],
  );

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      if (activeCategory.id) {
        fetchSubcategories(activeCategory.id);
      }
      return;
    }

    if (activeCategory.name) {
      const categoryToFetch = activeCategory.name === "All Blogs" ? null : activeCategory.name;
      fetchBlogs(
        categoryToFetch,
        currentPage,
        pageSize,
        searchQuery,
        selectedSubcategory?.id,
      );
      fetchSubcategories(activeCategory.id);

      // Only sync URL when not explicitly skipping (e.g., when clicking pagination)
      if (skipUrlSyncRef.current) {
        skipUrlSyncRef.current = false;
      } else {
        updateURL({
          category: activeCategory.name === "All Blogs" ? null : activeCategory.name,
          page: currentPage > 1 ? currentPage : null,
          search: searchQuery,
          subcategoryId: selectedSubcategory?.id,
        });
      }
    }
  }, [
    activeCategory,
    currentPage,
    searchQuery,
    selectedSubcategory,
    fetchBlogs,
    fetchSubcategories,
    updateURL,
  ]);


  useEffect(() => {
    if (initialData) {
      setBlogs(initialData.data || []);
      setTotalPage(initialData.meta?.pagination?.pageCount || 1);
    }
  }, [initialData]);

  const handleCategoryClick = (category) => {

    if (category.name === "All Blogs") {
      router.push("/blog");
    } else {
      const slug = category.slug || category.name.toLowerCase().replace(/\s+/g, "-");
      router.push(`/blog/${slug}`);
    }
  };

  const handleSubcategoryClick = (subcat) => {
    if (activeCategory.name && activeCategory.name !== "All Blogs") {
      const categorySlug = activeCategory.slug || activeCategory.name.toLowerCase().replace(/\s+/g, "-");
      const subcategorySlug = subcat.slug || subcat.name.toLowerCase().replace(/\s+/g, "-");
      router.push(`/blog/${categorySlug}/${subcategorySlug}`);
    }
  };

  const isAllBlogs = activeCategory.name === "All Blogs";

  // Link for previous and next page
  useEffect(() => {
    // Remove previously added pagination links
    const existingPrev = document.querySelector('link[rel="prev"]');
    const existingNext = document.querySelector('link[rel="next"]');

    existingPrev?.remove();
    existingNext?.remove();

    // Add previous page link
    if (currentPage > 1) {
      const prevLink = document.createElement("link");
      prevLink.rel = "prev";
      prevLink.href = buildHref(currentPage - 1);

      document.head.appendChild(prevLink);
    }

    // Add next page link
    if (currentPage < totalPages) {
      const nextLink = document.createElement("link");
      nextLink.rel = "next";
      nextLink.href = buildHref(currentPage + 1);

      document.head.appendChild(nextLink);
    }

    // Cleanup when page changes or component unmounts
    return () => {
      document.querySelector('link[rel="prev"]')?.remove();
      document.querySelector('link[rel="next"]')?.remove();
    };
  }, [currentPage, totalPages, buildHref]);
  return (
    <div className="w-full">
      <Banner title="Skill-Based Education, Career Guidance & Industry Insights | MSU Blog"/>
      <Breadcrumbs items={[{ label: "Blogs" }]} />

      <section className=" max-w-(--max-page-width) mx-auto px-(--container-padding) w-full my-6">
        {/* Mobile Search */}
        <div className="flex relative justify-center items-center pb-2 sm:hidden mb-4">
          <div className="flex items-center border border-slate-300 rounded overflow-hidden w-full">
            <input
              className="placeholder:text-slate-400 bg-white py-2 px-4 focus:outline-none w-full"
              placeholder="Search for anything..."
              type="text"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
              }}
            />
            <button
              onClick={() => setCurrentPage(1)}
              className="bg-[#007BAC] hover:bg-[#005D82] text-white px-4 py-3 flex items-center justify-center cursor-pointer h-full"
            >
              <Image
                src={SearchIcon}
                width={20}
                height={20}
                alt="Search icon"
                className="object-contain filter brightness-0 invert"
              />
            </button>
          </div>
        </div>

        {/* Desktop Search */}
        <div className="scrollbar-hidden flex items-center overflow-auto justify-between border-t-2 border-b-2 border-[#6167773d] pt-4">
          <Category
            categories={categories}
            handleCategory={handleCategoryClick}
            activeCategory={activeCategory.name}
          />
          <div className="flex items-center pb-2 mr-4 hidden sm:flex -mt-[10px]">
            <div className="flex items-center border border-slate-300 overflow-hidden">
              <input
                className="bg-white py-[9px] px-4 focus:outline-none w-64 placeholder:italic placeholder:text-slate-400"
                placeholder="Search for anything..."
                type="text"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                }}
              />
              <button
                onClick={() => setCurrentPage(1)}
                className="bg-[#007bac] hover:bg-[#005D82] text-white px-4 py-[10px] flex items-center justify-center cursor-pointer h-full"
              >
                Search
              </button>
            </div>
          </div> 
        </div>
      </section>

      <section className=" max-w-(--max-page-width) mx-auto px-(--container-padding) w-full my-6">
        <div className="flex justify-between items-center w-full mb-6">
          <h2 className="text-3xl font-bold text-gray-900">
            {activeCategory.name}
          </h2>
        </div>

        {isLoadingBlogs ? <BlogCardSkeleton /> : <BlogCards blogs={blogs} />}
      </section>

      <section className=" px-4 sm:px-6 lg:px-12 mx-auto w-full my-6">
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          setPage={(page) => {
            skipUrlSyncRef.current = true;
            setCurrentPage(page);
            window.scrollTo({ top: window.innerHeight * 0.5, behavior: "smooth" });
          }}
          buildHref={buildHref}
        />
      </section>

    </div>
  );
};

export default Blogs;